/**
 * @file theme-tokens.ts
 * @description Centralized CSS design tokens and typography constants for CountTheTime web client.
 * Isolated from DOM and Node.js dependencies for universal client/server portability.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

/*======================================================================
 * Theme Tokens & Design Constants
 *======================================================================*/

/**
 * Light theme design tokens for CountTheTime web client.
 */
export const LIGHT_THEME_TOKENS = {
  colors: {
    bgApp: '#f8fafc',
    bgCard: '#ffffff',
    borderCard: '#e2e8f0',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    badge404Bg: 'rgba(2, 132, 199, 0.12)',
    badge404Border: 'rgba(2, 132, 199, 0.28)',
    badge404Text: '#0284c7',
    badgeErrorBg: 'rgba(217, 119, 6, 0.12)',
    badgeErrorBorder: 'rgba(217, 119, 6, 0.28)',
    badgeErrorText: '#b45309',
    badgeActiveBg: 'rgba(34, 197, 94, 0.15)',
    badgeActiveBorder: 'rgba(34, 197, 94, 0.35)',
    badgeActiveText: '#15803d',
  },
  typography: {
    fontSans: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontMono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
} as const;

/**
 * Dark theme design tokens for CountTheTime web client.
 */
export const DARK_THEME_TOKENS = {
  colors: {
    bgApp: '#0f172a',
    bgCard: '#1e293b',
    borderCard: '#334155',
    textPrimary: '#f8fafc',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    badge404Bg: 'rgba(56, 189, 248, 0.1)',
    badge404Border: 'rgba(56, 189, 248, 0.2)',
    badge404Text: '#38bdf8',
    badgeErrorBg: 'rgba(245, 158, 11, 0.1)',
    badgeErrorBorder: 'rgba(245, 158, 11, 0.2)',
    badgeErrorText: '#f59e0b',
    badgeActiveBg: 'rgba(34, 197, 94, 0.12)',
    badgeActiveBorder: 'rgba(34, 197, 94, 0.28)',
    badgeActiveText: '#4ade80',
  },
  typography: {
    fontSans: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontMono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
} as const;

/**
 * Design token source of truth for display, dashboard, and dev tool styling (dark baseline).
 */
export const DISPLAY_THEME_TOKENS = DARK_THEME_TOKENS;

/**
 * Detect the initial theme ('light' or 'dark') based on user account preferences,
 * document attribute, window environment preference, or defaulting to 'light'.
 */
export function resolveInitialTheme(): 'light' | 'dark' {
  if (typeof localStorage !== 'undefined') {
    try {
      const userPrefsRaw = localStorage.getItem('ctt_user_preferences');
      if (userPrefsRaw) {
        const userPrefs = JSON.parse(userPrefsRaw);
        if (userPrefs && typeof userPrefs === 'object') {
          if (userPrefs.themePreference === 'dark') return 'dark';
          if (userPrefs.themePreference === 'light') return 'light';
        }
      }
      const savedTheme = localStorage.getItem('ctt_theme');
      if (savedTheme === 'dark') return 'dark';
      if (savedTheme === 'light') return 'light';
    } catch {
      // Ignore JSON parsing or storage access errors
    }
  }
  if (typeof document !== 'undefined' && document.documentElement) {
    const docTheme = document.documentElement.getAttribute('data-theme');
    if (docTheme === 'dark' || docTheme === 'light') {
      return docTheme;
    }
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  }
  return 'light';
}
